import React, { useCallback, useContext, useMemo } from "react";

import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import { APP_TOOLTIP_ID } from "@/src/constants/tooltip";

import CreateButton from "../common/CreateButton";
import {
  getProjectEditHref,
  getProjectHref,
  projectColumns,
} from "./tableParams/projectColumns";
import { GET_PROJECTS } from "../../actions/ActionTypes";
import { fetchApi } from "../../actions/FetchActions";
import useAbility from "../../hooks/useAbility";
import CappedPage from "../common/CappedPage";
import DataTable from "../dataTable/DataTable";
import { PROJECTS_ROUTE } from "../routes/routes";
import { AbilitiesContext } from "../utils/abilities/AbilitiesProvider";
import InfoPanel from "../utils/InfoPanel";

import type { JSX } from "react";

import type { AppState } from "../../reducers";
import type { ProjectRow } from "./tableParams/projectColumns.types";

const INFO_PANEL_LINKS = {
  en: "https://support.okast.tv/en/support/solutions/folders/103000304269",
  fr: "https://support.okast.tv/fr/support/solutions/folders/103000304269",
};

function ProjectList(): JSX.Element {
  const history = useHistory();
  const dispatch = useDispatch();
  const { canWriteOrRead, hasOrgaAbility } = useAbility();
  const abilities = useContext(AbilitiesContext);
  const { t } = useTranslation("editor-project");
  const token = useSelector((state: AppState) => state.loginReducer.token);
  const orgaConfig = useSelector(
    (state: AppState) => state.fetchReducer.orga_config,
  );
  const projects = useSelector(
    (state: AppState) => state.fetchReducer.projects,
  );

  const projectLimitReached =
    projects?.data.length >= orgaConfig?.data?.organization_options?.sfp_limit;

  const projectRowList = useMemo((): ProjectRow[] => {
    // Le reducer garde l'ancienne liste sur un échec.
    if (projects?.failed) return [];

    return (projects?.data ?? []).map((project) => ({
      ...project,
      status: project.enabled ? "active" : "stop",
      canEdit: canWriteOrRead("storefront_project", "write", project.abilities),
    }));
    // canWriteOrRead change à chaque rendu : les droits viennent du contexte.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects?.data, projects?.failed, abilities]);

  const fetchProjects = useCallback((): void => {
    dispatch(
      fetchApi({
        endpoint: "/api/project/v4/admin/storefront_projects",
        type: GET_PROJECTS,
        item: "projects",
        token,
      }),
    );
  }, [dispatch, token]);

  const columnList = useMemo(
    () =>
      projectColumns({
        t,
        onEdit: (project) => history.push(getProjectEditHref(project.name)),
      }),
    [t, history],
  );

  const getRowHref = useCallback(
    (project: ProjectRow): string => getProjectHref(project.name),
    [],
  );

  const createButton = hasOrgaAbility() ? (
    // Un bouton désactivé ne reçoit pas le survol : l'infobulle est sur le span.
    <span
      className="inline-flex"
      data-tooltip-id={projectLimitReached ? APP_TOOLTIP_ID : undefined}
      data-tooltip-content={
        projectLimitReached ? t("editor-errors:sfp-limit-reached") : undefined
      }
    >
      <CreateButton
        disabled={projectLimitReached}
        href={`${PROJECTS_ROUTE}/new`}
        label={t("createProject")}
      />
    </span>
  ) : undefined;

  return (
    <CappedPage contentClassName="pt-7.5 pb-15">
      <DataTable
        compactPagination
        columns={columnList}
        data={projectRowList}
        defaultPageSize={100}
        description={<InfoPanel links={INFO_PANEL_LINKS} />}
        getRowHref={getRowHref}
        headerActions={createButton}
        isError={projects?.failed}
        isLoading={!projects?.isFetched || projects?.isFetching}
        onRetry={fetchProjects}
        title={t("title")}
      />
    </CappedPage>
  );
}

export default ProjectList;
